// React Libraries
import React, { useState } from 'react';

//  Mui library components
import { Unstable_NumberInput as BaseNumberInput } from '@mui/base/Unstable_NumberInput';
import { styled } from '@mui/system';

// Material Icons
import { AddCounterValue, RemoveCounterValue } from '../../icons/MaterialIcon';

// Typography
import { Body2 } from '../../themes/typography';

// Context API - to get the ticketData
import { useTicketData } from '../../context/ticketDataContext';

const NumberInput = React.forwardRef(function CustomNumberInput(props, ref) {
    return (
        <BaseNumberInput
            slots={{
                root: StyledInputRoot,
                input: StyledInput,
                incrementButton: StyledButton,
                decrementButton: StyledButton
            }}
            slotProps={{
                incrementButton: {
                    children: <AddCounterValue fontSize="small" />,
                    className: 'increment'
                },
                decrementButton: {
                    children: <RemoveCounterValue fontSize="small" />
                }
            }}
            {...props}
            ref={ref}
        />
    );
});

// Component that allows users to give number input for ticket quantity selection
export default function TicketNumberInput({
    minQuantity,
    maxQuantity,
    ticketId,
    ticketName,
    ticketDiscountedPrice,
    ticketOriginalPrice,
    ticketDiscountRate,
    ticketQuantity,
    ticketSold,
    exceedQtyStatus,
    ticketDescription
}) {
    // State to store the value of the counter input
    const [valueOfCounter, setValueOfCounter] = useState(0);
    const [maxValue, setMaxValue] = useState(maxQuantity); // By default the maxQuantity value will be comes from maxPerUser from the ticket object

    // consume the addTicket function here from the context API
    const { addTicket } = useTicketData();

    // Function that handles the number value changes and update the value in a state
    function handleNumberValueChange(event, newValue) {
        setValueOfCounter(newValue); // Update the state value

        // created an object that holds the ticket name and quantity. quantity is just a counter value
        const ticketInfo =
            newValue > 0
                ? {
                      ticketName,
                      quantity: newValue,
                      ticketQuantity,
                      ticketSold,
                      ticketDiscountedPrice,
                      ticketOriginalPrice,
                      ticketDiscountRate,
                      exceedQtyStatus,
                      ticketDescription
                  }
                : 0;

        // tickedId and ticketInfo(with all the corresponding fields) will passed when reuse use this component in TicketListing component
        addTicket(ticketId, ticketInfo); // Add and update ticket info in the context API

        // get the value of actual available ticket quantity by subtracting the ticketSold from ticketQuantity
        let availableTicket = ticketQuantity - ticketSold;

        // Check if the exceedQtyStatus is true then set the max value to maxQuantity
        if (exceedQtyStatus === true) {
            setMaxValue(maxQuantity);
        }
        // Checks that who have the lower value between availableTicket and maxQuantity. and then assign that value to the maxValue state and then setMaxValue will update it. this will stop the counter
        else {
            if (availableTicket >= maxQuantity) {
                setMaxValue(maxQuantity);
            } else {
                setMaxValue(availableTicket);
            }
        }
    }

    // For debugging purpose
    // console.log('Outside Function', maxValue)

    return (
        <StyledContainer>
            <Body2>Quantity</Body2>
            <NumberInput
                min={minQuantity}
                max={maxValue}
                value={valueOfCounter}
                onChange={handleNumberValueChange}
            />
            <StyledMaxLimit>
                {/* Conditionally show messages based on maxValue. maxValue could be availableTicket or maxQuantity */}
                {(() => {
                    if (valueOfCounter === maxValue) {
                        if (maxValue === maxQuantity) {
                            return `Max Limit: ${maxValue}`;
                        } else {
                            return `Available Tickets: ${maxValue}`; // else (maxValue === availableTicket)
                        }
                    }
                })()}
            </StyledMaxLimit>
        </StyledContainer>
    );
}

// Default props for TicketNumberInput component
TicketNumberInput.defaultProps = {
    minQuantity: 0,
    maxQuantity: 5
};

// CSS styles for the components
const StyledInputRoot = styled('div')(
    `
  font-family: 'Noto Sans', sans-serif;
  font-weight: 400;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  gap: 10px;
`
);

const StyledInput = styled('input')(
    `
  font-size: 1.5rem;
  font-family: inherit;
  font-weight: 500;
  line-height: 1.375;
  color: #F8A21E;
  background: transparent;
  border: 2px solid gray;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.5);
  border-radius: 10px;
  padding: 10px 12px;
  outline: 0;
  min-width: 0;
  width: 4rem;
  text-align: center;

  &:hover {
    border-color: #F8A21E;
  }

  &:focus-visible {
    outline: 0;
  }
`
);

const StyledButton = styled('button')(
    `
  font-family: 'Noto Sans', sans-serif;
  font-size: 0.875rem;
  box-sizing: border-box;
  line-height: 1.5;
  border: 2px solid;
  border-radius: 999px;
  border-color: #F5F5F5;
  background: #F5F5F5;
  color: #11203A;
  width: 32px;
  height: 32px;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 120ms;

  &:hover {
    cursor: pointer;
    background: #F8A21E;
    border-color: #F5F5F5;
    color: #F5F5F5;
  }

  &:focus-visible {
    outline: 0;
  }

  &.increment {
    order: 1;
  }
`
);

const StyledContainer = styled('div')(
    `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 20px;
  height: 100%;
  // padding: 20px;
`
);

const StyledMaxLimit = styled('div')(
    `
  font-family: 'Noto Sans', sans-serif;
  font-weight: 400;
  font-size: 1rem;
  color: #F8A21E;
`
);
